/**
 * Data Helper Utilities
 *
 * Centralized utilities for data transformation and manipulation.
 * Consolidates logic from form-helpers.ts and list-utils.ts to eliminate duplication.
 */

/**
 * Format phone number for API submission (removes + prefix)
 * Use this when sending data to the backend
 *
 * @param phone - Phone number string (may include '+' prefix)
 * @returns Phone number without '+' prefix
 *
 * @example
 * formatPhoneForApi('+1234567890') // Returns: '1234567890'
 * formatPhoneForApi('1234567890')  // Returns: '1234567890'
 * formatPhoneForApi(undefined)     // Returns: undefined
 */
export function formatPhoneForApi(phone?: string): string | undefined {
  if (!phone) return phone;
  return phone.replace(/\+/g, '');
}

/**
 * Format phone number for display (adds + prefix if not present)
 * Use this when displaying data to users
 *
 * @param phone - Phone number string
 * @returns Phone number with '+' prefix
 *
 * @example
 * formatPhoneForDisplay('1234567890')  // Returns: '+1234567890'
 * formatPhoneForDisplay('+1234567890') // Returns: '+1234567890'
 * formatPhoneForDisplay(null)          // Returns: ''
 */
export function formatPhoneForDisplay(phone: string | null | undefined): string {
  if (!phone) return '';
  return phone.includes('+') ? phone : `+${phone}`;
}

/**
 * Converts empty strings to null in an object
 * This ensures consistent null handling in API payloads
 *
 * @param obj - Object with potential empty string values
 * @returns New object with empty strings converted to null
 *
 * @example
 * const data = { name: 'John', email: '', city: 'NYC' };
 * const cleaned = convertEmptyToNull(data);
 * // Returns: { name: 'John', email: null, city: 'NYC' }
 */
export function convertEmptyToNull(obj: Record<string, any>): Record<string, any> {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [key, value === '' ? null : value])
  );
}

/**
 * Converts null values to empty strings in an object
 * Useful for form initialization where inputs expect strings
 *
 * @param obj - Object with potential null values
 * @returns New object with null values converted to empty strings
 *
 * @example
 * const data = { name: 'John', email: null, city: 'NYC' };
 * const cleaned = convertNullToEmpty(data);
 * // Returns: { name: 'John', email: '', city: 'NYC' }
 */
export function convertNullToEmpty(obj: Record<string, any>): Record<string, any> {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [key, value === null ? '' : value])
  );
}

/**
 * Get updated fields by comparing original and updated objects
 * Only returns fields that have actually changed
 *
 * @param original - Original object before changes
 * @param updated - Updated object with potential changes
 * @returns Object containing only changed fields
 *
 * @example
 * const original = { name: 'John', email: 'john@example.com', age: 30 };
 * const updated = { name: 'John', email: 'newemail@example.com', age: 30 };
 * const changes = getUpdatedFields(original, updated);
 * // Returns: { email: 'newemail@example.com' }
 */
export function getUpdatedFields<T extends Record<string, any>>(
  original: Partial<T> | undefined,
  updated: Partial<T>
): Partial<T> {
  if (!original) return updated;

  return Object.keys(updated).reduce((acc, key) => {
    const typedKey = key as keyof T;
    const updatedValue = updated[typedKey];
    const originalValue = original[typedKey];

    if (updatedValue !== originalValue && updatedValue !== null) {
      acc[typedKey] = updatedValue;

      // Special case: if key is 'assignedId', also include 'assignedType'
      if (key === 'assignedId' && 'assignedType' in updated) {
        acc['assignedType' as keyof T] = updated['assignedType' as keyof T];
      }
    }

    return acc;
  }, {} as Partial<T>);
}
